/**
 * Shared builder: PL `FeaturedProductsShowcaseWidgetData` (the "featured products swimlane") → domain `CustomSection`.
 *
 * Mirrors `banner-section-builders.ts`: dependency-light, no JSX, so the swimlane widget can render
 * the existing `BackToSchool` section component (DS + legacy routing + product-card analytics)
 * without duplicating the proto→domain conversion.
 *
 * Product cards arrive as nested `Widget[]` (SINGLE_PRODUCT_CARD → productItem), enriched at PL serve time
 * exactly like PRODUCT_LIST / PRODUCT_GRID_CAROUSEL. The card shape mirrors product-grid
 * (`{ product: productItem }`, see product-grid-ds-widget.tsx).
 */

import type { IProductCard } from '@src/components/product-card/product-card.types'
import CustomSection from '@src/quince-site-common/src/domains/CustomSection'
import CUSTOM_SECTION_TYPE from '@src/quince-site-common/src/domains/CustomSectionType'

import { makeSection } from './banner-section-builders'
import { buildWidgetTrackingMetaData, protoButtonToDomain, toRichText } from './widget-utils'

import type { Widget } from '@lastbrand/storefront-proto-registry/api/v1/layoutfetch'
import type {
  FeaturedProductsShowcaseWidgetData,
  ProductCardData,
  WidgetChild,
} from '@lastbrand/storefront-proto-registry/api/v1/widget'

/** WidgetChild(PRODUCT_CARD → productItem)[] → the domain product-card shape the section renders. */
export function childrenToProductCards(children: WidgetChild[] = []): IProductCard[] {
  return children
    .map(child => (child.data as { productItem?: ProductCardData } | undefined)?.productItem)
    .filter((product): product is ProductCardData => Boolean(product))
    .map(product => ({ product }) as unknown as IProductCard)
}

/** Widget(SINGLE_PRODUCT_CARD → productItem)[] → the domain product-card shape the section renders. */
export function widgetsToProductCards(widgets: Widget[] = []): IProductCard[] {
  return widgets
    .map(widget => (widget.data as { productItem?: ProductCardData } | undefined)?.productItem)
    .filter((product): product is ProductCardData => Boolean(product))
    .map(product => ({ product }) as unknown as IProductCard)
}

export function buildBackToSchoolConfig(
  data: FeaturedProductsShowcaseWidgetData,
  productCards: IProductCard[],
): CustomSection {
  const heading = toRichText(data.heading as Parameters<typeof toRichText>[0])
  return makeSection({
    id: data.id,
    sectionType: CUSTOM_SECTION_TYPE.BACK_TO_SCHOOL,
    richTexts: (heading ? [heading] : []) as CustomSection['richTexts'],
    buttons: data.buttons.map(protoButtonToDomain) as CustomSection['buttons'],
    productCards,
    numberOfProductsToShowInDesktop: data.numberOfProductsToShowInDesktop,
    numberOfProductsToShowInMobile: data.numberOfProductsToShowInMobile,
    trackingMetaData: buildWidgetTrackingMetaData(data.analytics),
  })
}
